<script>
	import {
		handleMagicLinkRedirect,
		handleOAuthCallback,
		getSubscriptions
	} from '$lib/account.svelte.js';
	import Login from '$lib/components/login.svelte';
	import { userSession, theme } from '$lib/stores.svelte.js';
	import { onMount } from 'svelte';
	import '../app.css';
	import 'driver.js/dist/driver.css';
	import '../components.css';
	import PremiumModal from '$lib/components/modals/premiumModal.svelte';
	import MfaModal from '$lib/components/modals/mfaModal.svelte';
	import { browser } from '$app/environment';
	import { copyToClipboard, toastDisplay } from '$lib/utils.svelte.js';
	import posthog from 'posthog-js';
	import { beforeNavigate, afterNavigate, goto } from '$app/navigation';
	import { cookieConsent } from '$lib/cookieConsent.svelte.js';
	import { analytics } from '$lib/analytics.svelte.js';
	import CookieBanner from '$lib/components/CookieBanner.svelte';
	import CookiePreferencesIcon from '$lib/components/CookiePreferencesIcon.svelte';
	import SourceModal from '$lib/components/source/sourceModal.svelte';
	import ToastMachine from '$lib/components/toastMachine.svelte';
	import { loadStripe } from '@stripe/stripe-js/pure';
	import Modals from './Modals.svelte';
	import { openModal, closeModal } from '$lib/modal-store';
	import { initLocale, t } from '$lib/i18n.svelte.js';
	import { setValidatedTasks } from '$lib/components/training/trainingProgress.svelte.js';
	import { page } from '$app/state';

	/**
	 * @typedef {Object} Window
	 * @property {function(boolean): void} handleAccountClick - Handle account click event
	 * @property {function(): void} handleProClick - Handle pro subscription click
	 * @property {function(string): void} copyToClipboard - Copy text to clipboard
	 * @property {function(string, string, string): void} displaySource - Display a source modal
	 * @property {function(string, string, string): void} displaySourceGuess - Display a source guess modal
	 * @property {function(string[]): void} quoteTextOpenFunction - Open text with quotes
	 * @property {Object} [posthog] - PostHog analytics object
	 */

	onMount(() => {
		initLocale();
	});

	if (browser) {
		beforeNavigate(() => {
			// @ts-ignore
			if (cookieConsent.hasAnalyticsConsent && window.posthog) {
				// @ts-ignore
				posthog.capture('$pageleave');
			}
		});
		afterNavigate(() => {
			// @ts-ignore
			if (cookieConsent.hasAnalyticsConsent && window.posthog) {
				// @ts-ignore
				posthog.capture('$pageview');
			}
		});
	}

	onMount(async () => {
		document.body.classList.add('bg-ord-bgcolor');
		//inject({ mode: window.location.href.includes('localhost') ? 'development' : 'production' });
		if (browser && window.location.href.includes('#loggedin')) {
			toastDisplay('Connexion réussie !');
			await goto(window.location.pathname + window.location.search, { replaceState: true });
		}

		// Initialize analytics if user has already given consent
		if (cookieConsent.hasAnalyticsConsent) {
			analytics.initialize();
		}

		// Set up engagement detection for cookie banner
		if (!cookieConsent.hasUserEngaged()) {
			/** @type {NodeJS.Timeout | undefined} */
			let engagementTimer;
			let hasEngaged = false;
			const config = cookieConsent.getBannerConfig();

			const detectEngagement = () => {
				if (!hasEngaged) {
					hasEngaged = true;
					if (engagementTimer) clearTimeout(engagementTimer);
					// Use banner timing configuration
					setTimeout(() => {
						cookieConsent.markUserEngaged();
					}, config.timing);
				}
			};

			// Detect various engagement signals
			document.addEventListener('scroll', detectEngagement, { once: true });
			document.addEventListener('click', detectEngagement, { once: true });
			document.addEventListener('mousemove', detectEngagement, { once: true });
			document.addEventListener('touchstart', detectEngagement, { once: true });

			// Fallback: consider engaged after 5 seconds
			engagementTimer = setTimeout(() => {
				if (!hasEngaged) {
					cookieConsent.markUserEngaged();
				}
			}, 5000);
		}

		/**
		 *
		 * @param {string} sourceType
		 * @param {string} sourceId
		 * @param {string} passageIds
		 */
		//@ts-ignore
		window.displaySource = (sourceType, sourceId, passageIds) => {
			if (sourceType === 'user_doc') {
				const isDocumentOpen =
					document.querySelector(`#text-editor-${sourceId}`) !== null ||
					document.querySelector(`#source-modal-${sourceId}`) !== null;

				if (isDocumentOpen) {
					//@ts-ignore
					window.quoteTextOpenFunction(passageIds);
					return;
				}
			}

			openModal(SourceModal, {
				sourceType,
				sourceId,
				passageIds: passageIds ? passageIds.split(';') : undefined
			});
		};

		/**
		 *
		 * @param {string} sourceType
		 * @param {string} title
		 * @param {string} possibleSources (we will split on '†')
		 */
		// @ts-ignore
		window.displaySourceGuess = (sourceType, title, possibleSources) => {
			switch (sourceType) {
				case 'article':
					title = title
						.split(';')[0]
						.replaceAll(', ', '')
						.replaceAll('et ', '')
						.replaceAll('. ', '')
						.replaceAll('.', '')
						.replace(/([LRD])\s/g, '$1')
						.replace(/^articles?(\s+|\b)/i, '');

					openModal(SourceModal, {
						sourceId: undefined,
						sourceType: 'article',
						guessData: {
							title: title,
							possible_sources: possibleSources.split('†')
						}
					});
					break;
				case 'jurisprudence':
				case 'juris':
					openModal(SourceModal, {
						sourceId: undefined,
						sourceType: 'jurisprudence',
						guessData: {
							title: title,
							possible_sources: possibleSources.split('†')
						}
					});

					break;
			}
		};

		/**
		 * Handle account click event
		 * @param {boolean} signUp - Whether this is a sign up action
		 */
		// @ts-ignore
		window.handleAccountClick = function (signUp) {
			if ($userSession != null) {
				//window.location.href = '/dashboard';
			} else {
				openModal(Login, { signUpScreen: signUp });
			}
		};

		// @ts-ignore
		window.handleProClick = () => {
			openModal(PremiumModal);
		};

		window.addEventListener('pageshow', function (event) {
			var historyTraversal =
				event.persisted ||
				(typeof window.performance != 'undefined' && window.performance.navigation.type === 2);
			if (historyTraversal) {
				// Handle page restore.
				window.location.reload();
			}
		});

		/**
		 * Copy text to clipboard
		 * @param {string} text - Text to copy
		 */
		// @ts-ignore
		window.copyToClipboard = function (text) {
			copyToClipboard(text);
		};

		try {
			//@ts-ignore
			/** @type {any} */
			let res = await handleMagicLinkRedirect();
			if (!res) res = await handleOAuthCallback();
			if (res?.mfa_required) {
				console.log('mfa_required', res);
				openModal(MfaModal, {
					token: res.token,
					onSuccess: () => {
						window.location.href = '/app/chat#loggedin';
					}
				});
			} else if (res?.loggedIn) {
				toastDisplay(t('user.successLogin'), 'success');
				window.location.href = '/app/chat#loggedin';
			}
			await getSubscriptions();
		} catch (error) {}

		try {
			await loadStripe(
				'pk_live_51McXIvEZeSfMgOFQrpgUyvExT1Onxk0xpa5R8GS6i2h3WGOkQ1rqMyOpl8m73b79flgqMxfoD7DeTJ8UhBb2IHMf00o3qZyGtY'
			);
		} catch (error) {
			console.log('Error loading Stripe:', error);
		}

		/**
		 * @param {object} user - User session object
		 * @param {object} user.user_data - User data object
		 * @param {Array<any>} [user.user_data.training_progress] - User training progress
		 */
		userSession.subscribe(function (user) {
			if (user) {
				setValidatedTasks(user.user_data.training_progress ?? []);
			}
		});
	});

	/**
	 * Handle key down events for the window
	 * @param {*} event - The keyboard event
	 */
	const onKeyDown = function (event) {
		if (event.key === 'Escape' && document.getElementById('text-editor') == null) closeModal();
	};

	//let displayUploadScreen = false;

	//const handleDragOver = (e: any) => {
	//	e.preventDefault();
	//	if (e.dataTransfer && e.dataTransfer.types.includes('Files')) {
	//		if (document.getElementById('uploadScreenModal') == null) {
	//			displayUploadScreen = true;
	//		}
	//	}
	//};

	//$: displayUploadScreen;
</script>

<svelte:window onkeydown={onKeyDown} />

<svelte:head>
	<script
		async
		defer
		src="https://cdn.jsdelivr.net/npm/particles.js@2.0.0/particles.min.js"
	></script>
	<link rel="stylesheet" href="/hint.css" />
	<!-- <script
		async
		defer
		src="https://cdn.tolt.io/tolt.js"
		data-tolt="b5f7c3b8-6243-450a-befe-9275441e352d"
	></script> -->

	<!-- Cloudflare Analytics - loaded conditionally via analytics manager -->
</svelte:head>

<ToastMachine />
<CookieBanner />
<CookiePreferencesIcon />

{#if !page?.url?.pathname?.includes('/app')}
	<div class="pointer-events-none fixed top-0 left-0 z-[100] h-full w-full">
		<Modals />
	</div>
{/if}

<section class={$theme} lang={page.params.lang}>
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<slot />

	<div id="simpleToast"></div>
</section>

<style>
	.backdrop {
		position: fixed;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		transition: all 0.3s;
	}
</style>
